/**
 * Verifica se o domínio de um e-mail consegue receber mensagem (P-23,
 * ADR-0014).
 *
 * Roda no navegador, por DNS-over-HTTPS. Não há Edge Function no caminho de
 * propósito: um endpoint público nosso resolvendo DNS a pedido de qualquer um é
 * superfície que não precisa existir, e o resolvedor da Cloudflare responde com
 * CORS.
 *
 * **Falha em aberto.** Se o DNS não responder, a resposta é `indeterminado` e o
 * cadastro segue. Recusar alguém porque a nossa checagem teve problema é pior
 * que aceitar um endereço ruim — a verificação por e-mail ainda existe atrás
 * disto.
 */

const RESOLVEDOR = 'https://cloudflare-dns.com/dns-query';
const TEMPO_LIMITE_MS = 3000;

/** `NXDOMAIN` no protocolo: o domínio não existe. */
const NXDOMAIN = 3;

export type EstadoDoDominio =
  /** Tem MX, ou A como alternativa: aceita e-mail. */
  | 'entregavel'
  /** O domínio não existe. Quase sempre erro de digitação. */
  | 'inexistente'
  /** Existe, mas não anuncia como receber e-mail. */
  | 'sem-mx'
  /** Não deu para saber — DNS fora, rede ruim, tempo esgotado. */
  | 'indeterminado';

async function consultar(nome: string, tipo: 'MX' | 'A') {
  const controle = new AbortController();
  const relogio = setTimeout(() => controle.abort(), TEMPO_LIMITE_MS);

  try {
    const resposta = await fetch(`${RESOLVEDOR}?name=${encodeURIComponent(nome)}&type=${tipo}`, {
      headers: { accept: 'application/dns-json' },
      signal: controle.signal,
    });
    if (!resposta.ok) return null;
    return (await resposta.json()) as { Status?: number; Answer?: unknown[] };
  } catch {
    return null;
  } finally {
    clearTimeout(relogio);
  }
}

export async function verificarDominioDeEmail(email: string): Promise<EstadoDoDominio> {
  const dominio = email.trim().toLowerCase().split('@')[1];
  if (!dominio || !dominio.includes('.')) return 'indeterminado';

  const mx = await consultar(dominio, 'MX');
  if (!mx || typeof mx.Status !== 'number') return 'indeterminado';

  if (mx.Status === NXDOMAIN) return 'inexistente';
  if (mx.Status !== 0) return 'indeterminado';
  if ((mx.Answer ?? []).length > 0) return 'entregavel';

  // Sem MX o domínio ainda pode receber e-mail pelo registro A — a RFC 5321
  // prevê essa alternativa, e há domínio antigo que depende dela.
  const a = await consultar(dominio, 'A');
  if (!a || typeof a.Status !== 'number') return 'indeterminado';
  if (a.Status === 0 && (a.Answer ?? []).length > 0) return 'entregavel';

  return a.Status === NXDOMAIN ? 'inexistente' : 'sem-mx';
}
